import React from 'react';
import propTypes from 'prop-types';
import RichTextContent from 'tailwind-components/RichTextContent/RichTextContent';
import { cn } from 'lib/utils';

// header tone per derived oep status. matches StateUpdateChip's pastel palette
// so the detail panel reads as a natural extension of the clicked chip.
const HEADER_TONES = {
  Added: 'bg-green-100 text-green-800 border-green-200',
  Updated: 'bg-amber-100 text-amber-800 border-amber-200',
  Removed: 'bg-red-100 text-red-800 border-red-200'
};

/**
 * detail card shown below the expansions/exits chips when a chip is picked.
 * shows the state name plus the derived status in a colored header and the
 * counties as authored rich text (zoho html, or markdown when tag-free)
 * @param update - normalized oep state update from carrierFactory
 * @param aggregatedStatus - state-level status (Added/Updated/Removed) from
 *   buildStateStatusMap. preferred over update.status so the header matches
 *   the chip that opened it; falls back to update.status when not provided.
 */
export default function SelectedStateDetail({ update, aggregatedStatus }) {
  if (!update) return null;

  const status = aggregatedStatus || update.status;
  const tone = HEADER_TONES[status] || HEADER_TONES.Updated;

  return (
    <div className='flex min-w-0 flex-col rounded-lg border border-grey-lightest bg-white'>
      <div
        className={cn(
          'rounded-t-lg border-b px-3 py-2 text-center text-md font-semibold',
          tone
        )}
      >
        {update.stateName}
        <span className='ml-2 text-xs font-medium opacity-80'>· {status}</span>
      </div>
      <div className='min-w-0 px-3 py-2 text-md text-gray-700'>
        {renderBody(update)}
      </div>
    </div>
  );
}

// picks the copy shown under the header based on scope + counties. kept out
// of the render tree so the branching stays flat (no nested ternaries per
// CLAUDE.md).
function renderBody(update) {
  // the "entering" copy is only truthful on an Expansion. an Exit + New State
  // row is invalid data zoho should block, so it falls through to the generic
  // county branches rather than claiming the carrier is entering the state.
  if (update.type === 'Expansion' && update.scope === 'New State') {
    return renderNewStateBody(update);
  }
  if (update.scope === 'Whole State') {
    return (
      <p className='m-0 text-sm text-gray-600'>{wholeStateCopy(update.type)}</p>
    );
  }
  if (update.counties) {
    return <RichTextContent content={update.counties} compact />;
  }
  return (
    <p className='m-0 text-sm text-gray-400'>
      No counties listed for this state.
    </p>
  );
}

// a new state can be entered either in full or in a subset of counties, so the
// lead-in changes with whether the row carries a county list.
function renderNewStateBody(update) {
  if (!update.counties) {
    return (
      <p className='m-0 text-sm text-gray-600'>
        Entering the whole state — all counties included.
      </p>
    );
  }
  return (
    <>
      <p className='m-0 mb-1 text-sm text-gray-600'>
        Entering only in these counties:
      </p>
      <RichTextContent content={update.counties} compact />
    </>
  );
}

function wholeStateCopy(type) {
  if (type === 'Exit')
    return 'Exiting the whole state — all counties affected.';
  return 'Whole state affected — all counties included.';
}

SelectedStateDetail.propTypes = {
  update: propTypes.shape({
    type: propTypes.oneOf(['Expansion', 'Exit']).isRequired,
    status: propTypes.oneOf(['Added', 'Updated', 'Removed']).isRequired,
    scope: propTypes.oneOf(['New State', 'Whole State', 'Counties Only']),
    stateCode: propTypes.string.isRequired,
    stateName: propTypes.string,
    counties: propTypes.string
  }),
  aggregatedStatus: propTypes.oneOf(['Added', 'Updated', 'Removed'])
};
